import * as d3 from 'd3';
import {wrap} from "@/utils/d3Utils.js"

export default function SunburstChart(dataset, options) {

    const cfg = {
        color: d3.scaleOrdinal(d3.schemeCategory20)
    };

    copyOptions(options);

    function copyOptions(options) {
        if ('undefined' !== typeof options)
            for (let i in options) if (options.hasOwnProperty(i) && 'undefined' !== typeof options[i])
                cfg[i] = options[i];
    }

    let b, height, radius, y;

    const container = d3.select(options.container);

    const svg = container.append("svg");

    init();

    function init() {
        b = {
            w: cfg.width / (getSize(dataset) + 1), h: 30, s: 3, t: 10
        };

        height = cfg.height - b.h * 2;
        radius = Math.min(cfg.width, height) / 2;
        y = d3.scaleSqrt().range([0, radius]);
        svg.attr("width", cfg.width).attr("height", cfg.height);
    }

    const x = d3.scaleLinear().range([0, 2 * Math.PI]);

    function getSize(dataset) {
        let i = 0;
        while (dataset.children) {
            i++;
            dataset = dataset.children[0]
        }
        return i;
    }

    let vis;

    let h = [];
    let nodes;

    createVisualization(dataset);

    this.selectItem = id => {
        if (!nodes) return;

        let node = nodes.filter(d => d.data.id === id).shift();

        if (!node) return;

        mouseover(node);
    };


    function createVisualization(json) {

        initializeBreadcrumbTrail();

        vis = svg.append("g")
            .attr("transform", "translate(" + (cfg.width / 2) + "," + (height / 2) + ")");

        vis.append("svg:circle")
            .attr("r", radius)
            .style("fill", "#ffffff")
            .style("opacity", 1);

        json = d3.hierarchy(json);
        json.sum(d => d.value);

        nodes = d3.partition()(json).descendants();

        let i = 0;

        let minInnerRadius = Number.MAX_VALUE;

        vis.selectAll("path")
            .data(nodes)
            .enter()
            .append("svg:path")
            .attr("display", d => d.depth ? null : "none")
            .attr("class", "segment")
            .attr("d", d3.arc()
                .startAngle(d => Math.max(0, Math.min(2 * Math.PI, x(d.x0))))
                .endAngle(d => Math.max(0, Math.min(2 * Math.PI, x(d.x1))))
                .innerRadius(d => {
                    const r = Math.max(0, y(d.y0));
                    i++;
                    minInnerRadius = Math.min(minInnerRadius, r > 0 ? r : minInnerRadius);
                    if (i === nodes.length) {
                        const w = Math.sqrt(minInnerRadius * minInnerRadius / 2);
                        addTextBox(w, w);
                    }
                    return r
                })
                .outerRadius(d => Math.max(0, y(d.y1))))
            .attr("fill-rule", "evenodd")
            .style("fill", d => cfg.color((d.children ? d : d.parent).data.label))
            .style("opacity", 1)
            .on("mouseover", mouseover);

        function addTextBox(width, height) {
            const node = vis.append('g').attr("transform", "translate(" + (-width / 2) + "," + (-height / 2) + ")");

            const n = 10;

            let yOffset = height / 4;

            for (let i = 0; i < n; i++) {
                const th = 0.25 * height * (1 - ((i === 0) ? 0 : ((i + 12) / (3 * n)))) * ((i === 0) ? 1.5 : 1);

                h[i] = node.append('text');
                h[i].attr("dy", ".35em")
                    .attr('y', yOffset)
                    .attr("x", width / 2)
                    .attr("text-anchor", "middle")
                    .text("").attr("font-size", th).attr("fill", '#666666');

                yOffset += th * 1.2;
            }
        }

        setTransparent();
    }

    function initializeBreadcrumbTrail() {
        d3.select(cfg.sequence)
            .append("svg:svg")
            .attr("width", cfg.width)
            .attr("height", b.h)
            .attr("id", "trail")
            .append("svg:text")
            .attr("id", "endlabel")
            .style("fill", "#000000");
    }

    function mouseover(d) {
        for (let ht of h)
            ht.text("")

        h[0].text(d.value);

        let sequenceArray = getAncestors(d);

        for (let i = 0; i < sequenceArray.length; i++) {
            if ("undefined" === typeof h[i + 1]) break;
            h[i + 1].text(sequenceArray[i].data.label)
        }

        updateBreadcrumbs(sequenceArray, d.value);
        setTransparent();

        vis.selectAll("path").filter(node => (sequenceArray.indexOf(node) >= 0)).style("opacity", 1);

        highlightNode(d.children);

        function highlightNode(n) {
            if (!n) return;
            for (let c of n) {
                let sequenceArray = getAncestors(c);
                vis.selectAll("path").filter(node => (sequenceArray.indexOf(node) >= 0)).style("opacity", 1);
                highlightNode(c.children);
            }
        }
    }

    function setTransparent() {
        d3.selectAll("path.segment").style("opacity", 0.3);
    }

    function getAncestors(node) {
        const path = [];
        let current = node;
        while (current.parent) {
            path.unshift(current);
            current = current.parent;
        }
        return path;
    }

    function updateBreadcrumbs(nodeArray, value) {

        let g = d3.select("#trail")
            .selectAll("g")
            .data(nodeArray, d => d.data.label + d.depth);

        const entering = g.enter().append("svg:g");

        entering.append("svg:polygon")
            .attr("points", (d, i) => {
                const points = ["0,0", b.w + ",0", b.w + b.t + "," + (b.h / 2), b.w + "," + b.h, "0," + b.h];
                if (i > 0) points.push(b.t + "," + (b.h / 2));
                return points.join(" ");
            })
            .style("fill", d => cfg.color((d.children ? d : d.parent).data.label));

        entering.append("svg:text")
            .attr("x", (b.w + b.t) / 2)
            .attr("y", b.h * 0.45)
            .attr("dy", "0.45em")
            .attr("text-anchor", "middle")
            .text(d => d.data.label)
            .call(wrap, b.w * 0.9, b.h * 0.9);

        g.exit().remove();

        d3.select("#trail")
            .selectAll("g")
            .attr("transform", (d, i) => "translate(" + i * (b.w + b.s) + ", 0)")
            .exit()
            .remove();

        d3.select("#trail").select("#endlabel")
            .attr("x", (nodeArray.length) * (b.w + b.s) + 20)
            .attr("y", b.h / 2)
            .attr("dy", "0.45em")
            .attr("text-anchor", "left")
            .text(value);

        d3.select("#trail")
            .style("visibility", "");
    }
}
